// src/components/CompanyDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://sy47l7fjif.execute-api.us-east-1.amazonaws.com/Prod";
const COBOT_CLIENT_ID = "86097bc64f1d818fc779f1a3c83a7809";
const COBOT_REDIRECT_URI =
  "https://a6fvh67b5j.execute-api.us-east-1.amazonaws.com/Prod/store_token";

const fetchWithCompany = (url, options = {}) => {
  const { companyId: forcedCompanyId, ...fetchOptions } = options || {};

  let companyId = String(forcedCompanyId || "").trim();

  if (!companyId) {
    try {
      const ctx = JSON.parse(localStorage.getItem("company_context") || "{}");
      companyId =
        String(localStorage.getItem("company_id") || "").trim() ||
        String(ctx?.company_id || "").trim();
    } catch {
      companyId = String(localStorage.getItem("company_id") || "").trim();
    }
  }

  return fetch(url, {
    ...fetchOptions,
    headers: {
      ...(fetchOptions.headers || {}),
      "Content-Type":
        fetchOptions.headers?.["Content-Type"] ||
        fetchOptions.headers?.["content-type"] ||
        "application/json",
      ...(companyId ? { "x-company-id": companyId } : {}),
    },
  });
};

const Badge = ({ children, kind = "default" }) => {
  const base =
    "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border";
  const styles = {
    default: "bg-gray-100 text-gray-700 border-gray-200",
    green: "bg-green-100 text-green-700 border-green-200",
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    yellow: "bg-yellow-100 text-yellow-700 border-yellow-200",
    red: "bg-red-100 text-red-700 border-red-200",
  };

  return (
    <span className={`${base} ${styles[kind] || styles.default}`}>
      {children}
    </span>
  );
};

const TabButton = ({ active, children, onClick, kind = "default" }) => {
  const activeStyles = {
    default: "bg-gray-900 text-white border-gray-900",
    blue: "bg-blue-600 text-white border-blue-600",
    green: "bg-green-600 text-white border-green-600",
    yellow: "bg-yellow-500 text-white border-yellow-500",
  };

  const inactiveStyles =
    "bg-white text-gray-700 border-gray-200 hover:bg-gray-50";

  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${
        active ? activeStyles[kind] || activeStyles.default : inactiveStyles
      }`}
      type="button"
    >
      {children}
    </button>
  );
};

const StatCard = ({ title, value, hint, kind = "default" }) => {
  const accents = {
    default: "from-gray-50 to-white border-gray-200",
    blue: "from-blue-50 to-white border-blue-200",
    green: "from-green-50 to-white border-green-200",
    yellow: "from-yellow-50 to-white border-yellow-200",
    red: "from-red-50 to-white border-red-200",
  };

  return (
    <div
      className={`rounded-2xl border bg-gradient-to-br ${
        accents[kind] || accents.default
      } p-5 shadow-sm`}
    >
      <div className="text-sm text-gray-500">{title}</div>
      <div className="mt-2 text-3xl font-bold text-gray-900">{value}</div>
      {hint ? <div className="mt-2 text-sm text-gray-500">{hint}</div> : null}
    </div>
  );
};

const ProviderStatusBadge = ({ status }) => {
  const normalized = (status || "").toLowerCase();

  if (normalized === "active") return <Badge kind="green">aktiv</Badge>;
  if (normalized === "connected") return <Badge kind="green">verbunden</Badge>;
  if (normalized === "inactive") return <Badge kind="yellow">inaktiv</Badge>;
  if (normalized === "error") return <Badge kind="red">Fehler</Badge>;

  return <Badge>{status || "unbekannt"}</Badge>;
};

const ProviderTypeBadge = ({ provider }) => {
  const normalized = (provider || "").toLowerCase();

  if (normalized === "qonto") return <Badge kind="blue">Qonto</Badge>;
  if (normalized === "cobot") return <Badge kind="green">Cobot</Badge>;

  return <Badge>{provider || "Provider"}</Badge>;
};

const EmptyState = ({ title, text }) => (
  <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
    <div className="text-lg font-semibold text-gray-800">{title}</div>
    <p className="mt-2 text-sm text-gray-500">{text}</p>
  </div>
);

const ConnectCard = ({
  title,
  text,
  badge,
  badgeKind = "default",
  buttonText,
  onClick,
  primary = false,
}) => {
  return (
    <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="mt-2 text-sm text-gray-500">{text}</p>
        </div>
        {badge ? <Badge kind={badgeKind}>{badge}</Badge> : null}
      </div>

      <button
        type="button"
        onClick={onClick}
        className={`mt-5 w-full rounded-xl px-4 py-3 text-sm font-semibold transition ${
          primary
            ? "bg-blue-600 text-white hover:bg-blue-700"
            : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
        }`}
      >
        {buttonText}
      </button>
    </div>
  );
};

const AccordionSection = ({
  title,
  subtitle,
  badge,
  defaultOpen = false,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-6 py-5 hover:bg-gray-50 transition"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
              {badge || null}
            </div>
            {subtitle ? (
              <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
            ) : null}
          </div>

          <div className="text-2xl leading-none text-gray-400">
            {open ? "−" : "+"}
          </div>
        </div>
      </button>

      {open ? <div className="px-6 pb-6">{children}</div> : null}
    </div>
  );
};

const formatCurrency = (amount, currency = "EUR") => {
  const numeric = Number(amount || 0);
  try {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR",
    }).format(numeric);
  } catch {
    return `${numeric.toFixed(2)} ${currency || "EUR"}`;
  }
};

const formatDate = (dateValue) => {
  if (!dateValue) return "-";
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return dateValue;
  return d.toLocaleDateString("de-DE");
};

const formatShortDateTime = (dateValue) => {
  if (!dateValue) return "-";
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return dateValue;
  return d.toLocaleString("de-DE");
};

const getQontoRowId = (item) =>
  String(item?.external_invoice_id || item?.invoice_number || "");

const normalizeSlug = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const startCobotOAuth = (workspace) => {
  const normalizedWorkspace = normalizeSlug(workspace);

  if (!normalizedWorkspace) {
    alert("Bitte eine gültige Cobot-Subdomain angeben.");
    return false;
  }

  const redirectUri = encodeURIComponent(COBOT_REDIRECT_URI);
  const scope = encodeURIComponent(
    "read read_user invoices members list_spaces"
  );
  const state = encodeURIComponent(normalizedWorkspace);

  const authUrl =
    `https://www.cobot.me/oauth/authorize` +
    `?response_type=code` +
    `&client_id=${COBOT_CLIENT_ID}` +
    `&redirect_uri=${redirectUri}` +
    `&scope=${scope}` +
    `&state=${state}`;

  console.log("🚀 Cobot OAuth state:", normalizedWorkspace);
  console.log("🔗 Cobot OAuth URL:", authUrl);

  window.location.href = authUrl;
  return true;
};

const readCompanyContext = () => {
  try {
    return JSON.parse(localStorage.getItem("company_context") || "{}");
  } catch {
    return {};
  }
};

const resolveContext = () => {
  const ctx = readCompanyContext();

  const companyId =
    String(localStorage.getItem("company_id") || "").trim() ||
    String(ctx?.company_id || "").trim();

  const providerType =
    String(localStorage.getItem("provider_type") || "").trim() ||
    String(ctx?.provider_type || "").trim();

  const spaceId =
    String(localStorage.getItem("space_id") || "").trim() ||
    String(localStorage.getItem("SpaceID") || "").trim() ||
    String(localStorage.getItem("provider_context_id") || "").trim() ||
    String(ctx?.provider_context_id || "").trim();

  const companyName =
    String(localStorage.getItem("company_name") || "").trim() ||
    String(ctx?.legal_name || "").trim() ||
    String(ctx?.company_name || "").trim();

  return {
    companyId,
    providerType,
    spaceId,
    companyName,
    rawContext: ctx,
  };
};

const syncCompanyContext = ({ companyId, providerType, spaceId, legalName }) => {
  const existing = readCompanyContext();

  const existingCompanyId = String(localStorage.getItem("company_id") || "").trim();

  const merged = {
    ...existing,
    company_id: existingCompanyId || companyId || existing.company_id || null,
    provider_type: providerType || existing.provider_type || null,
    provider_context_id: spaceId || existing.provider_context_id || null,
    legal_name: legalName || existing.legal_name || existing.company_name || "",
    company_name:
      legalName || existing.company_name || existing.legal_name || "",
    completed: true,
  };

  if (!existingCompanyId && companyId) {
    localStorage.setItem("company_id", companyId);
  }

  if (providerType) localStorage.setItem("provider_type", providerType);

  if (spaceId) {
    localStorage.setItem("space_id", spaceId);
    localStorage.setItem("SpaceID", spaceId);
    localStorage.setItem("provider_context_id", spaceId);
  }

  if (legalName) localStorage.setItem("company_name", legalName);

  localStorage.setItem("company_context", JSON.stringify(merged));
};

const getCobotInvoiceId = (item) =>
  String(
    item?.id ||
      item?.invoice_id ||
      item?.invoice_number ||
      item?.number ||
      item?.uuid ||
      ""
  );

const getCobotInvoiceNumber = (item) =>
  item?.invoice_number || item?.number || item?.id || item?.invoice_id || "-";

const getCobotCustomerName = (item) => {
  const candidates = [
    item?.customer_display,
    item?.member?.name,
    item?.member?.company_name,
    item?.member_name,
    item?.member,
    item?.recipient?.name,
    item?.recipient_name,
    item?.customer?.name,
    item?.customer?.full_name,
    item?.customer_name,
    item?.contact?.name,
    item?.contact?.full_name,
    item?.contact_name,
    item?.company?.name,
    item?.company_name,
    item?.client?.name,
    item?.client?.full_name,
    item?.client_name,
    item?.debtor?.name,
    item?.debtor_name,
    item?.user?.name,
    item?.user?.full_name,
    item?.profile?.name,
    item?.name,
  ];

  const value = candidates.find(
    (v) => typeof v === "string" && v.trim().length > 0
  );

  return value || "-";
};

const getCobotDebtorEmail = (item) => {
  const candidates = [
    item?.debtor_email,
    item?.customer_email,
    item?.customer?.email,
    item?.member?.email,
    item?.recipient?.email,
    item?.contact?.email,
    item?.client?.email,
    item?.user?.email,
    item?.email,
  ];

  const value = candidates.find(
    (v) => typeof v === "string" && v.trim().length > 0
  );

  return value || null;
};

const getCobotDebtorCompany = (item) => {
  const candidates = [
    item?.debtor_company,
    item?.customer?.company_name,
    item?.customer?.company,
    item?.member?.company_name,
    item?.member?.company,
    item?.recipient?.company_name,
    item?.recipient?.company,
    item?.company?.name,
    item?.company_name,
    item?.client?.company_name,
    item?.client?.company,
  ];

  const value = candidates.find(
    (v) => typeof v === "string" && v.trim().length > 0
  );

  return value || null;
};

const getCobotSpaceName = (item, companyProfile, fallback = "") =>
  item?.space_name ||
  companyProfile?.space_name ||
  companyProfile?.legal_name ||
  companyProfile?.company_name ||
  fallback ||
  null;

const getCobotAmount = (item) =>
  item?.gross_amount || item?.total_amount || item?.amount || item?.price || 0;

const getCobotCurrency = (item) => item?.currency || "EUR";

const getCobotDueDate = (item) =>
  item?.due_date || item?.remind_at || item?.created_at || "";

const getCobotReminderLevel = (item) => {
  const value =
    item?.sent_reminders_count ?? item?.reminder_level ?? item?.reminders ?? 0;
  return Number(value || 0);
};

const getCobotRowStatus = (item, isSubmitted) => {
  if (isSubmitted) return "submitted";
  if (item?.inkasso_status) return String(item.inkasso_status).toLowerCase();
  return "inkasso_ready";
};

const isCobotTransferable = (item, isSubmitted) => {
  if (isSubmitted) return false;
  const status = String(item?.inkasso_status || "").toLowerCase();
  if (status === "submitted" || status === "submitted_to_inkasso") return false;
  if (status === "inkasso_active") return false;
  if (item?.inkasso_ready === false) return false;
  return !!getCobotInvoiceId(item);
};

const CompanyDashboard = () => {
  const navigate = useNavigate();

  const initial = resolveContext();

  const [providerType, setProviderType] = useState(initial.providerType);
  const [companyId, setCompanyId] = useState(initial.companyId);
  const [spaceId, setSpaceId] = useState(initial.spaceId);
  const [companyContext, setCompanyContext] = useState(
    localStorage.getItem("company_context") || ""
  );

  const [companyProfile, setCompanyProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedProvider, setSelectedProvider] = useState("all");

  const [mandantCasesPreview, setMandantCasesPreview] = useState([]);
  const [mandantCasesExport, setMandantCasesExport] = useState([]);
  const [mandantCasesStats, setMandantCasesStats] = useState({
    offeneAkten: 0,
    offeneHf: 0,
    offeneKosten: 0,
    offeneGesamt: 0,
    schuldnerAnzahl: 0,
    letzteAktualisierung: null,
  });

  const [mandantCasesLoading, setMandantCasesLoading] = useState(false);
  const [mandantCasesError, setMandantCasesError] = useState("");

  const [qontoCases, setQontoCases] = useState([]);
  const [qontoLoading, setQontoLoading] = useState(false);
  const [qontoError, setQontoError] = useState("");
  const [qontoFilters, setQontoFilters] = useState(null);

  const [selectedQontoIds, setSelectedQontoIds] = useState([]);
  const [qontoSubmitting, setQontoSubmitting] = useState(false);
  const [qontoSubmitError, setQontoSubmitError] = useState("");
  const [qontoSubmitInfo, setQontoSubmitInfo] = useState(null);
  const [submittedQontoIds, setSubmittedQontoIds] = useState({});

  const [cobotInvoices, setCobotInvoices] = useState([]);
  const [cobotLoading, setCobotLoading] = useState(false);
  const [cobotError, setCobotError] = useState("");
  const [cobotMeta, setCobotMeta] = useState(null);
  const [cobotFilters, setCobotFilters] = useState({
  minAgeDays: "",
  minReminderLevel: "",
});
  const [selectedCobotIds, setSelectedCobotIds] = useState([]);
  const [cobotSubmitting, setCobotSubmitting] = useState(false);
  const [cobotSubmitError, setCobotSubmitError] = useState("");
  const [cobotSubmitInfo, setCobotSubmitInfo] = useState(null);
  const [submittedCobotIds, setSubmittedCobotIds] = useState({});

  const parsedContext = useMemo(() => {
    try {
      return companyContext ? JSON.parse(companyContext) : null;
    } catch {
      return null;
    }
  }, [companyContext]);

  const companyName =
    companyProfile?.legal_name ||
    companyProfile?.company_name ||
    parsedContext?.legal_name ||
    parsedContext?.company_name ||
    initial.companyName ||
    "Mandant";

  const refreshLocalContext = () => {
    const next = resolveContext();
    setCompanyId(next.companyId);
    setProviderType(next.providerType);
    setSpaceId(next.spaceId);
    setCompanyContext(localStorage.getItem("company_context") || "");
    return next;
  };

  const loadCompanyProfileByCompanyId = async (forcedCompanyId = null) => {
    const currentCompanyId = String(
      forcedCompanyId || resolveContext().companyId || ""
    ).trim();

    if (!currentCompanyId) {
      setProfileLoading(false);
      setCompanyProfile(null);
      return null;
    }

    try {
      setProfileLoading(true);
      setError("");

      const res = await fetchWithCompany(
        `${API_BASE}/company?company_id=${encodeURIComponent(currentCompanyId)}`,
        {
          method: "GET",
          companyId: currentCompanyId,
        }
      );

      const rawText = await res.text();
      let data = null;

      try {
        data = rawText ? JSON.parse(rawText) : null;
      } catch {
        data = null;
      }

      if (!res.ok) {
        throw new Error(
          data?.error ||
            rawText ||
            `Firmenprofil konnte nicht geladen werden (HTTP ${res.status}).`
        );
      }

      const company = data?.company || data || null;
      setCompanyProfile(company);

      if (company) {
        const nextCompanyId = String(
          company.company_id || currentCompanyId
        ).trim();

        const nextLegalName = String(
          company.legal_name || company.company_name || ""
        ).trim();

        syncCompanyContext({
          companyId: nextCompanyId,
          providerType: providerType || resolveContext().providerType || "",
          spaceId: resolveContext().spaceId || company.space_id || "",
          legalName: nextLegalName,
        });

        setCompanyId(nextCompanyId);
        setCompanyContext(localStorage.getItem("company_context") || "");
      }

      return company;
    } catch (err) {
      setCompanyProfile(null);
      setError(err.message || "Firmenprofil konnte nicht geladen werden.");
      return null;
    } finally {
      setProfileLoading(false);
    }
  };

  const loadCompanyProfileBySpaceId = async (forcedSpaceId = null) => {
    const currentSpaceId = String(
      forcedSpaceId || resolveContext().spaceId || ""
    ).trim();

    if (!currentSpaceId) {
      setProfileLoading(false);
      setCompanyProfile(null);
      return null;
    }

    try {
      setProfileLoading(true);
      setError("");

      const res = await fetchWithCompany(
        `${API_BASE}/Cobot/Space?space_id=${encodeURIComponent(currentSpaceId)}`,
        {
          method: "GET",
          companyId: getEffectiveCompanyId(),
        }
      );

      const rawText = await res.text();
      let data = null;

      try {
        data = rawText ? JSON.parse(rawText) : null;
      } catch {
        data = null;
      }

      if (!res.ok) {
        throw new Error(
          data?.error ||
            rawText ||
            `Firmenprofil konnte nicht geladen werden (HTTP ${res.status}).`
        );
      }

      const space = data?.space || null;
      setCompanyProfile(space);

      if (space) {
        const nextCompanyId = getEffectiveCompanyId();
        const nextProviderType =
          String(space.provider_type || "").trim() || providerType || "cobot";
        const nextLegalName = String(
          space.legal_name || space.company_name || ""
        ).trim();

        syncCompanyContext({
          companyId: nextCompanyId,
          providerType: nextProviderType,
          spaceId: currentSpaceId,
          legalName: nextLegalName,
        });

        setCompanyId(nextCompanyId);
        setProviderType(nextProviderType);
        setSpaceId(currentSpaceId);
        setCompanyContext(localStorage.getItem("company_context") || "");
      }

      return space;
    } catch (err) {
      setCompanyProfile(null);
      setError(err.message || "Firmenprofil konnte nicht geladen werden.");
      return null;
    } finally {
      setProfileLoading(false);
    }
  };

  const getEffectiveCompanyId = () => {
    const current = resolveContext();

    if (current.companyId) return current.companyId;
    if (companyProfile?.company_id) return String(companyProfile.company_id).trim();
    return "";
  };

  const getEffectiveSpaceId = () => {
    const current = resolveContext();

    if (current.spaceId) return current.spaceId;
    if (companyProfile?.space_id) return String(companyProfile.space_id).trim();
    return "";
  };

  const loadQontoCases = async (forcedCompanyId = null) => {
    const effectiveCompanyId =
      String(forcedCompanyId || getEffectiveCompanyId() || "").trim();

    if (!effectiveCompanyId) {
      setQontoCases([]);
      setQontoFilters(null);
      setQontoError(
        "Keine company_id gefunden. Bitte zuerst das Firmenprofil speichern."
      );
      return;
    }

    try {
      setQontoLoading(true);
      setQontoError("");

      const res = await fetchWithCompany(
        `${API_BASE}/qonto?company_id=${encodeURIComponent(
          effectiveCompanyId
        )}&mode=inkasso_ready&only_unpaid=true&exclude_cancelled=true&only_overdue=true`,
        {
          method: "GET",
          companyId: effectiveCompanyId,
        }
      );
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Fehler beim Laden der Qonto-Daten");
      }

      const list = data.cases_preview || [];
      setQontoCases(list);
      setQontoFilters(data.applied_filters || null);

      setSelectedQontoIds((prev) =>
        prev.filter((id) => list.some((item) => getQontoRowId(item) === id))
      );
    } catch (err) {
      setQontoCases([]);
      setQontoError(err.message || "Qonto-Daten konnten nicht geladen werden.");
    } finally {
      setQontoLoading(false);
    }
  };

  const loadSubmittedCobotCases = async (
    forcedCompanyId = null,
    forcedSpaceId = null
  ) => {
    const effectiveCompanyId = String(
      forcedCompanyId || getEffectiveCompanyId() || ""
    ).trim();

    const effectiveSpaceId = String(
      forcedSpaceId || getEffectiveSpaceId() || ""
    ).trim();

    if (!effectiveCompanyId && !effectiveSpaceId) {
      setSubmittedCobotIds({});
      return {};
    }

    try {
      const params = new URLSearchParams();
      if (effectiveCompanyId) params.set("company_id", effectiveCompanyId);
      if (effectiveSpaceId) params.set("space_id", effectiveSpaceId);
      params.set("provider", "cobot");

      const res = await fetchWithCompany(`${API_BASE}/inkasso?${params.toString()}`, {
        method: "GET",
        companyId: effectiveCompanyId,
      });
      const rawText = await res.text();

      let data = null;
      try {
        data = rawText ? JSON.parse(rawText) : null;
      } catch {
        data = null;
      }

      if (!res.ok) {
        throw new Error(
          data?.error ||
            rawText ||
            `Inkasso-Fälle konnten nicht geladen werden (HTTP ${res.status}).`
        );
      }

      const items = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.cases)
        ? data.cases
        : Array.isArray(data)
        ? data
        : [];

      const submittedMap = {};
      items.forEach((item) => {
        const invoiceId = String(item?.invoice_id || "").trim();
        const status = String(item?.status || "").toLowerCase().trim();

        if (!invoiceId) return;

        if (
          [
            "new",
            "submitted",
            "accepted",
            "confirmed",
            "exported",
            "inkasso_active",
          ].includes(status)
        ) {
          submittedMap[invoiceId] = true;
        }
      });

      setSubmittedCobotIds(submittedMap);
      setSelectedCobotIds((prev) => prev.filter((id) => !submittedMap[id]));

      return submittedMap;
    } catch (err) {
      console.error("Fehler beim Laden bereits übergebener Cobot-Fälle:", err);
      return {};
    }
  };

  const loadCobotInvoices = async (
    forcedSpaceId = null,
    forcedFilters = null
  ) => {
    const effectiveCompanyId = String(getEffectiveCompanyId() || "").trim();
    const effectiveSpaceId = String(
      forcedSpaceId || getEffectiveSpaceId() || ""
    ).trim();

    const effectiveFilters = forcedFilters || cobotFilters;
    const minAgeDays = Number(effectiveFilters?.minAgeDays || 0);
    const minReminderLevel = Number(effectiveFilters?.minReminderLevel || 0);

    if (!effectiveSpaceId) {
      setCobotInvoices([]);
      setCobotMeta(null);
      setCobotError(
        "Kein Cobot-Space vorhanden. Bitte zuerst Cobot verbinden."
      );
      return;
    }

    if (!effectiveCompanyId) {
      setCobotInvoices([]);
      setCobotMeta(null);
      setCobotError(
        "Keine company_id gefunden. Bitte zuerst das Firmenprofil speichern."
      );
      return;
    }

    try {
      setCobotLoading(true);
      setCobotError("");
      setCobotSubmitError("");
      setCobotSubmitInfo(null);

    const res = await fetch(`${API_BASE}/invoices`, {
      method: "POST",
      headers: {
       "Content-Type": "application/json",
       "x-company-id": effectiveCompanyId,
    },
    body: JSON.stringify({
        space_id: effectiveSpaceId,
        minAgeDays,
        minReminderLevel,
  }),
});

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data?.error || data?.detail || "Fehler beim Laden der Cobot-Rechnungen"
        );
      }

      const nextInvoices = Array.isArray(data?.invoices) ? data.invoices : [];
      setCobotInvoices(nextInvoices);
      setCobotMeta(data?.meta || null);

      const submittedMap = await loadSubmittedCobotCases(
        getEffectiveCompanyId(),
        effectiveSpaceId
      );

      setSelectedCobotIds((prev) =>
        prev.filter((id) => {
          const stillExists = nextInvoices.some(
            (item) => getCobotInvoiceId(item) === id
          );
          const alreadySubmitted = !!submittedMap[id];
          return stillExists && !alreadySubmitted;
        })
      );
    } catch (err) {
      setCobotInvoices([]);
      setCobotMeta(null);
      setCobotError(
        err.message || "Cobot-Rechnungen konnten nicht geladen werden."
      );
    } finally {
      setCobotLoading(false);
    }
  };

  const loadConnections = async (forcedCompanyId = null) => {
    const effectiveCompanyId =
      String(forcedCompanyId || getEffectiveCompanyId() || "").trim();

    if (!effectiveCompanyId) {
      setConnections([]);
      setLoading(false);
      setError("Keine company_id gefunden. Bitte zuerst das Firmenprofil speichern.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      syncCompanyContext({
        companyId: effectiveCompanyId,
        providerType: providerType || "cobot",
        spaceId: getEffectiveSpaceId(),
        legalName:
          companyProfile?.legal_name ||
          companyProfile?.company_name ||
          companyName,
      });

      const res = await fetchWithCompany(
        `${API_BASE}/provider_connection?company_id=${encodeURIComponent(
          effectiveCompanyId
        )}`,
        {
          method: "GET",
        }
      );

      const rawText = await res.text();
      let data = null;

      try {
        data = rawText ? JSON.parse(rawText) : null;
      } catch {
        data = null;
      }

      if (!res.ok) {
        throw new Error(
          data?.error || rawText || "Fehler beim Laden der Provider-Verbindungen"
        );
      }

      const items = Array.isArray(data?.items) ? data.items : [];
      setConnections(items);

      const qontoIsConnected = items.some(
        (c) =>
          (c.provider || "").toLowerCase() === "qonto" &&
          ["active", "connected"].includes((c.status || "").toLowerCase())
      );

      const cobotIsConnected =
        (providerType || "").toLowerCase() === "cobot" || !!getEffectiveSpaceId();

      if (cobotIsConnected) {
        const alreadyExists = items.some(
          (c) => (c.provider || "").toLowerCase() === "cobot"
        );

        if (!alreadyExists) {
          const currentSpaceId = getEffectiveSpaceId();
          setConnections((prev) => [
            {
              connection_id: `cobot-${currentSpaceId || "local"}`,
              provider: "cobot",
              status: "connected",
              external_id: currentSpaceId || "-",
              secret_name: "stored_in_cobot_tokens",
              source: "derived_from_space_profile",
            },
            ...prev,
          ]);
        }
      }

      if (qontoIsConnected) {
        await loadQontoCases(effectiveCompanyId);
      } else {
        setQontoCases([]);
        setQontoFilters(null);
        setQontoError("");
        setSelectedQontoIds([]);
        setSubmittedQontoIds({});
      }
    } catch (err) {
      setConnections([]);
      setError(err.message || "Unbekannter Fehler");
    } finally {
      setLoading(false);
    }
  };

  const loadMandantCasesPreview = async () => {
    const effectiveCompanyId = getEffectiveCompanyId();

    if (!effectiveCompanyId) {
      setMandantCasesPreview([]);
      setMandantCasesExport([]);
      setMandantCasesStats({
        offeneAkten: 0,
        offeneHf: 0,
        offeneKosten: 0,
        offeneGesamt: 0,
        schuldnerAnzahl: 0,
        letzteAktualisierung: null,
      });
      setMandantCasesError("");
      return;
    }

    try {
      setMandantCasesLoading(true);
      setMandantCasesError("");

      const [statsRes, previewRes, exportRes] = await Promise.all([
        fetchWithCompany(
          `${API_BASE}/mandant/cases/stats?company_id=${encodeURIComponent(
            effectiveCompanyId
          )}`,
          {
            method: "GET",
          }
        ),
        fetchWithCompany(
          `${API_BASE}/mandant/cases?company_id=${encodeURIComponent(
            effectiveCompanyId
          )}&limit=5`,
          {
            method: "GET",
          }
        ),
        fetchWithCompany(
          `${API_BASE}/mandant/cases?company_id=${encodeURIComponent(
            effectiveCompanyId
          )}&limit=5000`,
          {
            method: "GET",
          }
        ),
      ]);

      const statsData = await statsRes.json();
      const previewData = await previewRes.json();
      const exportData = await exportRes.json();

      if (!statsRes.ok || statsData?.ok !== true) {
        throw new Error(
          statsData?.error || "Statistik konnte nicht geladen werden."
        );
      }

      if (!previewRes.ok) {
        throw new Error(
          previewData?.error || "Aktenvorschau konnte nicht geladen werden."
        );
      }

      if (!exportRes.ok) {
        throw new Error(
          exportData?.error || "Aktenexport konnte nicht geladen werden."
        );
      }

      const stats = statsData?.stats || {};

      setMandantCasesStats({
        offeneAkten: Number(stats.offene_akten || 0),
        offeneHf: Number(stats.summe_hf || 0),
        offeneKosten: Number(stats.summe_kosten || 0),
        offeneGesamt: Number(stats.summe_gesamt || 0),
        schuldnerAnzahl: Number(stats.schuldner_anzahl || 0),
        letzteAktualisierung: stats.letzte_aktualisierung || null,
      });

      const previewItems = Array.isArray(previewData?.items)
        ? previewData.items
        : Array.isArray(previewData?.cases)
        ? previewData.cases
        : Array.isArray(previewData)
        ? previewData
        : [];

      setMandantCasesPreview(previewItems);

      const exportItems = Array.isArray(exportData?.items)
        ? exportData.items
        : Array.isArray(exportData?.cases)
        ? exportData.cases
        : Array.isArray(exportData)
        ? exportData
        : [];

      setMandantCasesExport(exportItems);
    } catch (err) {
      setMandantCasesPreview([]);
      setMandantCasesExport([]);
      setMandantCasesStats({
        offeneAkten: 0,
        offeneHf: 0,
        offeneKosten: 0,
        offeneGesamt: 0,
        schuldnerAnzahl: 0,
        letzteAktualisierung: null,
      });
      setMandantCasesError(
        err.message || "Aktenvorschau konnte nicht geladen werden."
      );
    } finally {
      setMandantCasesLoading(false);
    }
  };

  useEffect(() => {
    let active = true;

    async function bootstrap() {
      const current = refreshLocalContext();
      const currentCompanyId = current.companyId;
      const currentSpaceId = current.spaceId;

      if (currentCompanyId) {
        const company = await loadCompanyProfileByCompanyId(currentCompanyId);
        if (!active) return;

        const effectiveCompanyId =
          String(company?.company_id || resolveContext().companyId || "").trim();

        if (effectiveCompanyId) {
          setCompanyId(effectiveCompanyId);
          await loadConnections(effectiveCompanyId);
          await loadMandantCasesPreview();
          return;
        }
      }

      if (currentSpaceId) {
        const profile = await loadCompanyProfileBySpaceId(currentSpaceId);
        if (!active) return;

        const effectiveCompanyId =
          String(profile?.company_id || resolveContext().companyId || "").trim();

        if (effectiveCompanyId) {
          setCompanyId(effectiveCompanyId);
          await loadConnections(effectiveCompanyId);
          await loadMandantCasesPreview();
          return;
        }

        setLoading(false);
        setError("Keine company_id gefunden. Bitte zuerst das Firmenprofil speichern.");
        return;
      }

      setProfileLoading(false);
      setLoading(false);
      setError(
        "Kein Company- oder Space-Kontext gefunden. Bitte zuerst das Firmenprofil speichern."
      );
    }

    bootstrap();

    return () => {
      active = false;
    };
  }, []);

  const hasQonto = connections.some(
    (c) =>
      (c.provider || "").toLowerCase() === "qonto" &&
      ["active", "connected"].includes((c.status || "").toLowerCase())
  );

  const hasCobot =
    !!getEffectiveSpaceId() ||
    connections.some(
      (c) =>
        (c.provider || "").toLowerCase() === "cobot" &&
        ["active", "connected"].includes((c.status || "").toLowerCase())
    );

  const activeConnections = connections.filter((c) =>
    ["active", "connected"].includes((c.status || "").toLowerCase())
  ).length;

  const qontoConnections = connections.filter(
    (c) => (c.provider || "").toLowerCase() === "qonto"
  ).length;

  const cobotConnections = hasCobot
    ? Math.max(
        1,
        connections.filter((c) => (c.provider || "").toLowerCase() === "cobot").length
      )
    : 0;

  const qontoTotalAmount = qontoCases.reduce(
    (sum, item) => sum + Number(item.amount_total || 0),
    0
  );

  const qontoSelectedAmount = qontoCases
    .filter((item) => selectedQontoIds.includes(getQontoRowId(item)))
    .reduce((sum, item) => sum + Number(item.amount_total || 0), 0);

  const qontoMaxDays = qontoCases.reduce(
    (max, item) => Math.max(max, Number(item.days_overdue || 0)),
    0
  );

  const allSelectableQontoIds = qontoCases
    .map((item) => getQontoRowId(item))
    .filter(Boolean)
    .filter((id) => !submittedQontoIds[id]);

  const allSelectableChecked =
    allSelectableQontoIds.length > 0 &&
    allSelectableQontoIds.every((id) => selectedQontoIds.includes(id));

  const cobotTotalAmount = cobotInvoices.reduce(
    (sum, item) => sum + Number(getCobotAmount(item) || 0),
    0
  );

  const cobotSelectedAmount = cobotInvoices
    .filter((item) => selectedCobotIds.includes(getCobotInvoiceId(item)))
    .reduce((sum, item) => sum + Number(getCobotAmount(item) || 0), 0);

  const allSelectableCobotIds = cobotInvoices
    .filter((item) =>
      isCobotTransferable(item, !!submittedCobotIds[getCobotInvoiceId(item)])
    )
    .map((item) => getCobotInvoiceId(item))
    .filter(Boolean);

  const allSelectableCobotChecked =
    allSelectableCobotIds.length > 0 &&
    allSelectableCobotIds.every((id) => selectedCobotIds.includes(id));

  const handleLogout = () => {
    localStorage.removeItem("company_id");
    localStorage.removeItem("provider_type");
    localStorage.removeItem("space_id");
    localStorage.removeItem("SpaceID");
    localStorage.removeItem("provider_context_id");
    localStorage.removeItem("company_name");
    localStorage.removeItem("company_context");
    localStorage.removeItem("connect_mode");
    localStorage.removeItem("return_to");

    setProviderType("");
    setCompanyId("");
    setSpaceId("");
    setCompanyContext("");
    setCompanyProfile(null);
    setConnections([]);
    setQontoCases([]);
    setCobotInvoices([]);
    setSelectedQontoIds([]);
    setSelectedCobotIds([]);
    setSubmittedQontoIds({});
    setSubmittedCobotIds({});
    setError("");

    navigate("/");
  };

  const handleConnectCobot = () => {
    const effectiveCompanyId = getEffectiveCompanyId();

    if (!effectiveCompanyId) {
      alert("Bitte zuerst ein Firmenprofil mit company_id anlegen.");
      return;
    }

    const existingWorkspace =
      localStorage.getItem("provider_context_id") ||
      localStorage.getItem("space_id") ||
      localStorage.getItem("SpaceID") ||
      getEffectiveSpaceId() ||
      "";

    localStorage.setItem("provider_type", "cobot");
    localStorage.setItem("company_id", effectiveCompanyId);
    localStorage.setItem("connect_mode", "attach_cobot");
    localStorage.setItem("return_to", "/company-dashboard");

    setProviderType("cobot");

    if (existingWorkspace) {
      localStorage.setItem("provider_context_id", existingWorkspace);
      localStorage.setItem("space_id", existingWorkspace);
      localStorage.setItem("SpaceID", existingWorkspace);
      startCobotOAuth(existingWorkspace);
      return;
    }

    const enteredWorkspace = window.prompt(
      "Bitte Cobot Space / Subdomain eingeben (z. B. hello-coworking)"
    );

    if (!enteredWorkspace) {
      return;
    }

    const normalizedWorkspace = normalizeSlug(enteredWorkspace);

    if (!normalizedWorkspace) {
      alert("Bitte eine gültige Cobot-Subdomain angeben.");
      return;
    }

    localStorage.setItem("provider_context_id", normalizedWorkspace);
    localStorage.setItem("space_id", normalizedWorkspace);
    localStorage.setItem("SpaceID", normalizedWorkspace);
    setSpaceId(normalizedWorkspace);

    startCobotOAuth(normalizedWorkspace);
  };

  const handleConnectQonto = async () => {
    const effectiveCompanyId = getEffectiveCompanyId();

    if (!effectiveCompanyId) {
      alert("Bitte zuerst ein Firmenprofil mit company_id anlegen.");
      return;
    }

    const signIn = window.prompt("Qonto Sign-In / E-Mail");
    if (!signIn) return;

    const secretKey = window.prompt("Qonto Secret Key");
    if (!secretKey) return;

    const organizationId = window.prompt("Qonto Organization ID");
    if (!organizationId) return;

    try {
      const res = await fetchWithCompany(`${API_BASE}/qonto/connect`, {
        method: "POST",
        companyId: effectiveCompanyId,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          company_id: effectiveCompanyId,
          sign_in: signIn,
          secret_key: secretKey,
          organization_id: organizationId,
        }),
      });

      const data = await res.json();

      if (!res.ok || data?.ok !== true) {
        alert(data?.error || data?.message || "Qonto Verbindung fehlgeschlagen.");
        return;
      }

      localStorage.setItem("provider_type", "qonto");
      setProviderType("qonto");

      syncCompanyContext({
        companyId: effectiveCompanyId,
        providerType: "qonto",
        spaceId: getEffectiveSpaceId(),
        legalName:
          companyProfile?.legal_name ||
          companyProfile?.company_name ||
          companyName,
      });

      setCompanyId(effectiveCompanyId);
      setCompanyContext(localStorage.getItem("company_context") || "");

      await loadConnections(effectiveCompanyId);
      await loadQontoCases(effectiveCompanyId);
      setSelectedProvider("qonto");
      alert("Qonto erfolgreich verbunden.");
    } catch (err) {
      alert("Qonto Verbindung fehlgeschlagen.");
      console.error(err);
    }
  };

  const handleOpenProfile = () => {
    navigate("/profile");
  };

  const toggleQontoInvoice = (rowId) => {
    if (!rowId || submittedQontoIds[rowId]) return;

    setSelectedQontoIds((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
    );
  };

  const toggleAllQontoInvoices = (checked) => {
    if (!checked) {
      setSelectedQontoIds([]);
      return;
    }
    setSelectedQontoIds(allSelectableQontoIds);
  };

  const submitSelectedQontoToInkasso = async () => {
    if (!selectedQontoIds.length) {
      setQontoSubmitError("Bitte zuerst mindestens eine Qonto-Rechnung auswählen.");
      return;
    }

    const effectiveCompanyId = getEffectiveCompanyId();
    if (!effectiveCompanyId) {
      setQontoSubmitError(
        "Keine company_id gefunden. Bitte zuerst das Firmenprofil speichern."
      );
      return;
    }

    try {
      setQontoSubmitting(true);
      setQontoSubmitError("");
      setQontoSubmitInfo(null);

      const res = await fetchWithCompany(`${API_BASE}/qonto/submit-to-inkasso`, {
        method: "POST",
        companyId: effectiveCompanyId,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company_id: effectiveCompanyId,
          invoice_ids: selectedQontoIds,
          submitted_by: "company_dashboard",
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Fehler bei der Übergabe an Inkasso");
      }

      const created = Array.isArray(data.created) ? data.created : [];
      const updated = Array.isArray(data.updated) ? data.updated : [];

      const createdIds = created
        .map((item) => item?.external_invoice_id || item?.invoice_id || item?.id)
        .filter(Boolean);

      const updatedIds = updated
        .map((item) => item?.external_invoice_id || item?.invoice_id || item?.id)
        .filter(Boolean);

      const mergedSubmitted = {};
      [...createdIds, ...updatedIds, ...selectedQontoIds].forEach((id) => {
        if (id) mergedSubmitted[String(id)] = true;
      });

      setSubmittedQontoIds((prev) => ({ ...prev, ...mergedSubmitted }));

      setQontoSubmitInfo({
        createdCount: data.created_count ?? created.length,
        updatedCount: data.updated_count ?? updated.length,
        skippedCount: data.skipped_count ?? 0,
      });

      setSelectedQontoIds([]);
      await loadQontoCases(effectiveCompanyId);
    } catch (err) {
      setQontoSubmitError(err.message || "Übergabe an Inkasso fehlgeschlagen.");
    } finally {
      setQontoSubmitting(false);
    }
  };

  const toggleCobotInvoice = (rowId) => {
    if (!rowId || submittedCobotIds[rowId]) return;

    setSelectedCobotIds((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
    );
  };

  const toggleAllCobotInvoices = (checked) => {
    if (!checked) {
      setSelectedCobotIds([]);
      return;
    }
    setSelectedCobotIds(allSelectableCobotIds);
  };

  const submitSelectedCobotToInkasso = async (idsOverride = null) => {
  const invoiceIds = Array.isArray(idsOverride) ? idsOverride : selectedCobotIds;

  if (!invoiceIds.length) {
    setCobotSubmitError("Bitte zuerst mindestens eine Cobot-Rechnung auswählen.");
    return;
  }

  const effectiveCompanyId = getEffectiveCompanyId();
  const effectiveSpaceId = getEffectiveSpaceId();

  if (!effectiveCompanyId) {
    setCobotSubmitError(
      "Keine company_id gefunden. Bitte zuerst das Firmenprofil speichern."
    );
    return;
  }

  if (!effectiveSpaceId) {
    setCobotSubmitError(
      "Keine space_id gefunden. Bitte zuerst den Cobot-Kontext prüfen."
    );
    return;
  }

  const confirmed = window.confirm(
    `${invoiceIds.length} Cobot-Rechnung(en) wirklich an Inkasso übergeben?`
  );
  if (!confirmed) return;

  try {
    setCobotSubmitting(true);
    setCobotSubmitError("");
    setCobotSubmitInfo(null);

    const selectedInvoices = cobotInvoices.filter((item) =>
      invoiceIds.includes(getCobotInvoiceId(item))
    );

    if (!selectedInvoices.length) {
      throw new Error("Die ausgewählten Cobot-Rechnungen konnten nicht gefunden werden.");
    }

    const createdInvoiceIds = [];
    const duplicateInvoiceIds = [];
    const errors = [];

    for (const item of selectedInvoices) {
      const rowId = getCobotInvoiceId(item);

      if (!rowId) {
        errors.push({
          invoice_ids: [],
          error: "Rechnung ohne ID kann nicht übergeben werden.",
        });
        continue;
      }

      const payload = {
        company_id: effectiveCompanyId,
        space_id: effectiveSpaceId,
        space_name: getCobotSpaceName(item, companyProfile, companyName),
        provider: "cobot",
        invoice_ids: [rowId],
        invoice_number: getCobotInvoiceNumber(item),
        debtor_name: getCobotCustomerName(item),
        debtor_email: getCobotDebtorEmail(item),
        debtor_company: getCobotDebtorCompany(item),
        amount: Number(getCobotAmount(item) || 0),
        currency: getCobotCurrency(item),
        due_date: getCobotDueDate(item),
        cobot_reminder_level: getCobotReminderLevel(item),
        submitted_by: "company_dashboard",
        payload: item,
      };

      try {
        const res = await fetchWithCompany(`${API_BASE}/inkasso`, {
          method: "POST",
          companyId: effectiveCompanyId,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(
            data?.error || data?.message || "Fehler bei der Übergabe an Inkasso"
          );
        }

        const createdCount = Number(data?.created_count || 0);
        const duplicateCount = Number(data?.duplicate_count || 0);
        const errorCount = Number(data?.error_count || 0);

        if (createdCount > 0) {
          createdInvoiceIds.push(rowId);
        } else if (duplicateCount > 0) {
          duplicateInvoiceIds.push(rowId);
        }

        if (errorCount > 0 && Array.isArray(data?.errors)) {
          data.errors.forEach((entry) => errors.push(entry));
        }
      } catch (err) {
        errors.push({
          invoice_ids: [rowId],
          error: err.message || "Unbekannter Fehler",
        });
      }
    }

    const submittedMap = {};
    [...createdInvoiceIds, ...duplicateInvoiceIds].forEach((id) => {
      if (id) submittedMap[String(id)] = true;
    });

    setSubmittedCobotIds((prev) => ({ ...prev, ...submittedMap }));
    setSelectedCobotIds((prev) => prev.filter((id) => !submittedMap[id]));

    setCobotSubmitInfo({
      createdCount: createdInvoiceIds.length,
      duplicateCount: duplicateInvoiceIds.length,
      errorCount: errors.length,
    });

    if (errors.length > 0) {
      setCobotSubmitError(
        `${errors.length} Rechnung(en) konnten nicht sauber übergeben werden.`
      );
    }

    await loadCobotInvoices(effectiveSpaceId, cobotFilters);
  } catch (err) {
    setCobotSubmitError(
      err.message || "Übergabe der Cobot-Rechnungen an Inkasso fehlgeschlagen."
    );
  } finally {
    setCobotSubmitting(false);
  }
};

  const handleReloadAll = async () => {
    const current = refreshLocalContext();
    const effectiveCompanyId = current.companyId || getEffectiveCompanyId();
    const effectiveSpaceId = current.spaceId || getEffectiveSpaceId();

    if (effectiveCompanyId) {
      await loadCompanyProfileByCompanyId(effectiveCompanyId);
      await loadConnections(effectiveCompanyId);
      await loadMandantCasesPreview();
      return;
    }

    if (effectiveSpaceId) {
      await loadCompanyProfileBySpaceId(effectiveSpaceId);
      const nextCompanyId = getEffectiveCompanyId();

      if (nextCompanyId) {
        await loadConnections(nextCompanyId);
        await loadMandantCasesPreview();
      }

      return;
    }
  };

  const handleCobotFilterChange = (e) => {
  const { name, value } = e.target;

  setCobotFilters((prev) => ({
    ...prev,
    [name]: value === "" ? "" : String(Number(value)),
  }));
};

  const handleLoadCobotInvoices = async () => {
    await loadCobotInvoices(getEffectiveSpaceId(), cobotFilters);
    setSelectedProvider("cobot-invoices");
  };

  const handleExportMandantCasesCSV = () => {
    if (!mandantCasesExport || mandantCasesExport.length === 0) {
      alert("Keine Akten zum Export vorhanden.");
      return;
    }

    const headers = [
      "AZ",
      "Mandant AZ",
      "Schuldner",
      "Ort",
      "Stadium",
      "Bearbeitungsstatus",
      "Offene HF",
      "Übernommene HF",
      "Auslagen",
      "Letztes Ereignis",
      "Letztes Datum",
      "Abschlussdatum",
    ];

    const rows = mandantCasesExport.map((c) => [
      c.az || "",
      c.mandant_az || "",
      c.schuldner || "",
      c.ort || "",
      c.stadium || "",
      c.bearb_status || "",
      c.offene_hf ?? 0,
      c.uebern_hf ?? 0,
      c.uebern_kosten ?? 0,
      c.last_event_text || "",
      c.last_event_date || "",
      c.abschlussdatum || "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((val) => `"${String(val ?? "").replace(/"/g, '""')}"`)
          .join(";")
      )
      .join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeCompany = (companyName || "mandant")
      .replace(/[^a-zA-Z0-9-_]+/g, "_")
      .replace(/^_+|_+$/g, "");

    link.href = url;
    link.setAttribute("download", `${safeCompany}_akten_export.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="DI Inkasso" className="h-10" />
            <div>
              <div className="text-2xl font-bold text-gray-900">
                Company Dashboard
              </div>
              <div className="text-sm text-gray-500">
                Mandantenübersicht, Provider-Verbindungen und Inkasso-Steuerung
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {providerType ? <ProviderTypeBadge provider={providerType} /> : null}
            {companyId ? (
              <Badge kind="green">Mandant aktiv</Badge>
            ) : (
              <Badge kind="red">Kein Mandant</Badge>
            )}
            {spaceId ? <Badge>Cobot-Kontext vorhanden</Badge> : null}

            <button
              onClick={handleLogout}
              type="button"
              className="ml-0 md:ml-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pt-8">
        <div className="rounded-3xl bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white shadow-lg overflow-hidden">
          <div className="px-6 py-8 md:px-8 md:py-10">
            <div className="text-sm uppercase tracking-wide text-gray-300">
              Inkasso Plattform
            </div>
            <h1 className="mt-2 text-2xl md:text-3xl font-bold">
              Willkommen, {companyName}
            </h1>
            <p className="mt-3 text-sm md:text-base text-gray-300 max-w-3xl">
              Hier steuerst du alle angebundenen Quellen zentral an einem Ort —
              von Cobot über Qonto bis zu den übergebenen Inkasso-Fällen.
            </p>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
              <div className="bg-white/10 rounded-2xl p-4 border border-white/10">
                <div className="text-gray-300">Company ID</div>
                <div className="mt-1 font-mono break-all text-white">
                  {companyId || "noch nicht gesetzt"}
                </div>
              </div>

              <div className="bg-white/10 rounded-2xl p-4 border border-white/10">
                <div className="text-gray-300">Space ID</div>
                <div className="mt-1 font-mono break-all text-white">
                  {spaceId || "kein Cobot-Kontext"}
                </div>
              </div>

              <div className="bg-white/10 rounded-2xl p-4 border border-white/10">
                <div className="text-gray-300">Primärer Kontext</div>
                <div className="mt-1 text-white">
                  {providerType || "manuell / offen"}
                </div>
              </div>

              <div className="bg-white/10 rounded-2xl p-4 border border-white/10">
                <div className="text-gray-300">Firmenprofil</div>
                <div className="mt-1 text-white">
                  {parsedContext?.completed
                    ? "vollständig gespeichert"
                    : "teilweise / offen"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {(loading || profileLoading) && (
          <div className="rounded-2xl bg-white border shadow-sm p-6">
            <div className="text-gray-700 font-medium">Lade Daten ...</div>
            <div className="mt-2 text-sm text-gray-500">
              Firmenprofil und Provider-Verbindungen werden geladen.
            </div>
          </div>
        )}

        {!loading && !profileLoading && error && (
          <div className="rounded-2xl bg-red-50 border border-red-200 shadow-sm p-5 mb-8">
            <div className="text-red-800 font-semibold">Fehler</div>
            <div className="mt-1 text-sm text-red-700">{error}</div>
          </div>
        )}

        {!loading && !profileLoading && !error && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <StatCard
                title="Provider gesamt"
                value={connections.length}
                hint="Alle erkannten Verbindungen"
              />
              <StatCard
                title="Aktive Provider"
                value={activeConnections}
                hint="Derzeit nutzbare Verbindungen"
                kind="green"
              />
              <StatCard
                title="Qonto"
                value={qontoConnections}
                hint="Anbindungen für Rechnungen"
                kind="blue"
              />
              <StatCard
                title="Cobot"
                value={cobotConnections}
                hint="Abgeleitet aus Firmenprofil / Provider-Verbindungen"
                kind="yellow"
              />
            </div>

            <AccordionSection
              title="Firmenprofil"
              subtitle="Das gespeicherte Profil aus dem Cobot-/Company-Flow."
              badge={
                companyProfile?.inkasso_agb_accepted ? (
                  <Badge kind="green">Profil vorhanden</Badge>
                ) : (
                  <Badge kind="yellow">Bitte prüfen</Badge>
                )
              }
              defaultOpen
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
                <div />
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={handleOpenProfile}
                    type="button"
                    className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Firmenprofil öffnen
                  </button>

                  <button
                    onClick={handleReloadAll}
                    type="button"
                    className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Alles neu laden
                  </button>
                </div>
              </div>

              {!companyProfile ? (
                <EmptyState
                  title="Kein Firmenprofil geladen"
                  text="Bitte prüfe, ob das Firmenprofil bereits gespeichert wurde."
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  <div className="rounded-2xl border border-gray-200 p-4 bg-gray-50">
                    <div className="text-sm text-gray-500">Rechtlicher Name</div>
                    <div className="mt-1 font-semibold text-gray-900">
                      {companyProfile.legal_name ||
                        companyProfile.company_name ||
                        "-"}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 p-4 bg-gray-50">
                    <div className="text-sm text-gray-500">Company ID</div>
                    <div className="mt-1 font-mono text-gray-900 break-all">
                      {companyProfile.company_id || "-"}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 p-4 bg-gray-50">
                    <div className="text-sm text-gray-500">Space ID</div>
                    <div className="mt-1 font-mono text-gray-900 break-all">
                      {companyProfile.space_id || spaceId || "-"}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 p-4 bg-gray-50">
                    <div className="text-sm text-gray-500">E-Mail</div>
                    <div className="mt-1 text-gray-900">
                      {companyProfile.contact_email || "-"}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 p-4 bg-gray-50">
                    <div className="text-sm text-gray-500">Telefon</div>
                    <div className="mt-1 text-gray-900">
                      {companyProfile.contact_phone || "-"}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 p-4 bg-gray-50">
                    <div className="text-sm text-gray-500">IBAN</div>
                    <div className="mt-1 font-mono text-gray-900 break-all">
                      {companyProfile.iban || "-"}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 p-4 bg-gray-50 md:col-span-2 xl:col-span-2">
                    <div className="text-sm text-gray-500">Adresse</div>
                    <div className="mt-1 text-gray-900">
                      {[
                        companyProfile.address_street,
                        companyProfile.address_zip,
                        companyProfile.address_city,
                        companyProfile.address_country,
                      ]
                        .filter(Boolean)
                        .join(", ") || "-"}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 p-4 bg-gray-50">
                    <div className="text-sm text-gray-500">AGB Status</div>
                    <div className="mt-1">
                      {companyProfile.inkasso_agb_accepted ? (
                        <Badge kind="green">akzeptiert</Badge>
                      ) : (
                        <Badge kind="yellow">offen</Badge>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </AccordionSection>

            <AccordionSection
              title="Provider verbinden"
              subtitle="Weitere Datenquellen für diesen Mandanten ergänzen."
              badge={<Badge kind="blue">Connectoren</Badge>}
              defaultOpen
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
                <div />
                <button
                  onClick={handleOpenProfile}
                  type="button"
                  className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Firmenprofil öffnen
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <ConnectCard
                  title="Cobot anbinden"
                  text="Für Coworking Spaces, Members, Mahnungen und bestehende Space-basierte Prozesse."
                  badge={hasCobot ? "bereits aktiv" : "verfügbar"}
                  badgeKind={hasCobot ? "green" : "default"}
                  buttonText={
                    hasCobot ? "Cobot erneut verbinden" : "Mit Cobot verbinden"
                  }
                  onClick={handleConnectCobot}
                  primary={!hasCobot}
                />

                <ConnectCard
                  title="Qonto anbinden"
                  text="Für Rechnungen, Inkasso-Kandidaten und die zentrale Verarbeitung von Qonto-Daten."
                  badge={hasQonto ? "bereits aktiv" : "verfügbar"}
                  badgeKind={hasQonto ? "blue" : "default"}
                  buttonText={
                    hasQonto ? "Qonto erneut verbinden" : "Mit Qonto verbinden"
                  }
                  onClick={handleConnectQonto}
                />

                <ConnectCard
                  title="Manueller Start"
                  text="Nutze die Plattform zunächst ohne weitere Tool-Verbindung und ergänze Provider später."
                  badge="optional"
                  badgeKind="yellow"
                  buttonText="Firmenprofil bearbeiten"
                  onClick={handleOpenProfile}
                />
              </div>
            </AccordionSection>

            <AccordionSection
              title="Verbundene Provider"
              subtitle="Übersicht aller Verbindungen auf Mandantenebene."
              badge={<Badge kind="green">{activeConnections} aktiv</Badge>}
            >
              <div className="flex justify-end mb-5">
                <button
                  onClick={() => loadConnections(getEffectiveCompanyId())}
                  type="button"
                  className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Neu laden
                </button>
              </div>

              {connections.length === 0 ? (
                <EmptyState
                  title="Noch keine zusätzlichen Provider verbunden"
                  text="Cobot ist bereits über das gespeicherte Firmenprofil vorhanden. Weitere Connectoren wie Qonto erscheinen hier, sobald sie verbunden sind."
                />
              ) : (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  {connections.map((conn) => (
                    <div
                      key={conn.connection_id}
                      className="rounded-2xl border border-gray-200 p-5 bg-gradient-to-br from-white to-gray-50"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <ProviderTypeBadge provider={conn.provider} />
                            <ProviderStatusBadge status={conn.status} />
                          </div>
                          <div className="mt-3 text-lg font-semibold text-gray-900">
                            {(conn.provider || "Provider").toUpperCase()}
                          </div>
                        </div>

                        <div className="text-right text-xs text-gray-400">
                          <div>Connection ID</div>
                          <div className="font-mono text-gray-500 break-all max-w-[180px]">
                            {conn.connection_id || "-"}
                          </div>
                        </div>
                      </div>

                      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="rounded-xl bg-white border border-gray-200 p-3">
                          <div className="text-gray-500">Provider</div>
                          <div className="mt-1 font-medium text-gray-900">
                            {conn.provider || "-"}
                          </div>
                        </div>

                        <div className="rounded-xl bg-white border border-gray-200 p-3">
                          <div className="text-gray-500">Status</div>
                          <div className="mt-1 font-medium text-gray-900">
                            {conn.status || "-"}
                          </div>
                        </div>

                        <div className="rounded-xl bg-white border border-gray-200 p-3 md:col-span-2">
                          <div className="text-gray-500">External ID</div>
                          <div className="mt-1 font-mono text-gray-900 break-all">
                            {conn.external_id || "-"}
                          </div>
                        </div>

                        <div className="rounded-xl bg-white border border-gray-200 p-3 md:col-span-2">
                          <div className="text-gray-500">Secret / Referenz</div>
                          <div className="mt-1 font-mono text-gray-900 break-all">
                            {conn.secret_name ||
                              conn.secret_reference ||
                              conn.source ||
                              "-"}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </AccordionSection>

            <AccordionSection
              title="Bereiche"
              subtitle="Navigation für die nächsten Ausbaustufen des Dashboards."
              badge={<Badge kind="yellow">Navigation</Badge>}
              defaultOpen
            >
              <div className="flex gap-2 flex-wrap">
                <TabButton
                  active={selectedProvider === "all"}
                  onClick={() => setSelectedProvider("all")}
                >
                  Übersicht
                </TabButton>

                {hasQonto && (
                  <TabButton
                    active={selectedProvider === "qonto"}
                    onClick={() => setSelectedProvider("qonto")}
                    kind="blue"
                  >
                    Qonto
                  </TabButton>
                )}

                {hasCobot && (
                  <TabButton
                    active={selectedProvider === "cobot"}
                    onClick={() => setSelectedProvider("cobot")}
                    kind="green"
                  >
                    Cobot
                  </TabButton>
                )}

                {hasCobot && (
                  <TabButton
                    active={selectedProvider === "cobot-invoices"}
                    onClick={() => setSelectedProvider("cobot-invoices")}
                    kind="green"
                  >
                    Cobot Rechnungen
                  </TabButton>
                )}

                <TabButton
                  active={selectedProvider === "cases"}
                  onClick={() => setSelectedProvider("cases")}
                  kind="yellow"
                >
                  Inkasso Cases
                </TabButton>
              </div>
            </AccordionSection>

            <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-6">
              {selectedProvider === "all" && (
                <div>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        Übersicht
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Zentraler Einstieg in die neue mandantenfähige Architektur.
                      </p>
                    </div>
                    <Badge kind="green">Basis steht</Badge>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                    <div className="rounded-2xl border border-gray-200 p-5 bg-gray-50">
                      <div className="text-sm font-medium text-gray-800">
                        1. Company-Kontext
                      </div>
                      <p className="mt-2 text-sm text-gray-500">
                        Das Dashboard lädt bevorzugt das gespeicherte Firmenprofil
                        über die company_id. Falls nur ein Cobot-Kontext vorhanden
                        ist, wird ersatzweise über die space_id geladen.
                      </p>
                    </div>

                    <div className="rounded-2xl border border-gray-200 p-5 bg-gray-50">
                      <div className="text-sm font-medium text-gray-800">
                        2. Provider-Verbindungen
                      </div>
                      <p className="mt-2 text-sm text-gray-500">
                        Cobot und Qonto werden als Connectoren unter dem Mandanten
                        geführt.
                      </p>
                    </div>

                    <div className="rounded-2xl border border-gray-200 p-5 bg-gray-50">
                      <div className="text-sm font-medium text-gray-800">
                        3. Inkasso-Fälle
                      </div>
                      <p className="mt-2 text-sm text-gray-500">
                        Im Dashboard zeigen wir nur die Kennzahlen. Die Details
                        liegen in der separaten Aktenübersicht.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {hasQonto && (
                      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5">
                        <div className="flex items-center justify-between gap-3 mb-4">
                          <div>
                            <h3 className="text-lg font-semibold text-blue-900">
                              Qonto Schnellüberblick
                            </h3>
                            <p className="text-sm text-blue-800 mt-1">
                              Erste produktive Daten aus dem Qonto-Connector.
                            </p>
                          </div>
                          <ProviderTypeBadge provider="qonto" />
                        </div>

                        {qontoLoading ? (
                          <div className="text-sm text-blue-800">
                            Qonto-Daten werden geladen ...
                          </div>
                        ) : qontoError ? (
                          <div className="text-sm text-red-700">{qontoError}</div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <StatCard
                              title="Inkasso-Kandidaten"
                              value={qontoCases.length}
                              hint="Aus Qonto / mode=inkasso_ready"
                              kind="blue"
                            />
                            <StatCard
                              title="Gesamtvolumen"
                              value={formatCurrency(qontoTotalAmount, "EUR")}
                              hint="Summe aller Kandidaten"
                              kind="blue"
                            />
                            <StatCard
                              title="Max. Überfälligkeit"
                              value={`${qontoMaxDays} Tage`}
                              hint="Höchster Wert in der Liste"
                              kind="yellow"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {hasCobot && (
                      <div className="rounded-2xl border border-green-200 bg-green-50 p-5">
                        <div className="flex items-center justify-between gap-3 mb-4">
                          <div>
                            <h3 className="text-lg font-semibold text-green-900">
                              Cobot Schnellüberblick
                            </h3>
                            <p className="text-sm text-green-800 mt-1">
                              Offene Cobot-Rechnungen lassen sich im separaten Tab
                              nach Alter und Mahnstufe filtern.
                            </p>
                          </div>
                          <ProviderTypeBadge provider="cobot" />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <StatCard
                            title="Space"
                            value={getEffectiveSpaceId() || "-"}
                            hint="Aktiver Cobot-Kontext"
                            kind="green"
                          />
                          <StatCard
                            title="Geladene Rechnungen"
                            value={cobotInvoices.length}
                            hint="Nur nach aktivem Abruf"
                            kind="green"
                          />
                          <StatCard
                            title="Volumen"
                            value={formatCurrency(cobotTotalAmount, "EUR")}
                            hint="Summiert aus geladener Liste"
                            kind="yellow"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedProvider === "qonto" && (
                <div>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Qonto</h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Inkasso-Kandidaten aus Qonto auf Basis von{" "}
                        <code>mode=inkasso_ready</code>.
                      </p>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      <ProviderTypeBadge provider="qonto" />
                      <button
                        onClick={() => loadQontoCases(getEffectiveCompanyId())}
                        type="button"
                        className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Qonto neu laden
                      </button>
                    </div>
                  </div>

                  {qontoLoading && (
                    <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5 text-sm text-blue-900">
                      Qonto-Daten werden geladen ...
                    </div>
                  )}

                  {!qontoLoading && qontoError && (
                    <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
                      {qontoError}
                    </div>
                  )}

                  {!qontoLoading && !qontoError && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <StatCard
                          title="Inkasso-Kandidaten"
                          value={qontoCases.length}
                          hint="Qonto Fälle mit Inkasso-Relevanz"
                          kind="blue"
                        />
                        <StatCard
                          title="Gesamtbetrag"
                          value={formatCurrency(qontoTotalAmount, "EUR")}
                          hint="Summiertes Volumen"
                          kind="green"
                        />
                        <StatCard
                          title="Ausgewählt"
                          value={selectedQontoIds.length}
                          hint="Für Übergabe markiert"
                          kind="yellow"
                        />
                        <StatCard
                          title="Auswahlwert"
                          value={formatCurrency(qontoSelectedAmount, "EUR")}
                          hint="Volumen der Auswahl"
                          kind="blue"
                        />
                      </div>

                      {qontoFilters && (
                        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 mb-6">
                          <div className="text-sm font-semibold text-gray-800 mb-2">
                            Aktive Filter
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Badge kind="blue">
                              only_unpaid: {String(qontoFilters.only_unpaid)}
                            </Badge>
                            <Badge kind="blue">
                              only_overdue: {String(qontoFilters.only_overdue)}
                            </Badge>
                            <Badge kind="blue">
                              exclude_cancelled:{" "}
                              {String(qontoFilters.exclude_cancelled)}
                            </Badge>
                            <Badge kind="yellow">
                              min_days_overdue: {qontoFilters.min_days_overdue}
                            </Badge>
                            <Badge kind="yellow">
                              min_reminder_level: {qontoFilters.min_reminder_level}
                            </Badge>
                          </div>
                        </div>
                      )}

                      {qontoSubmitError ? (
                        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 mb-6 text-sm text-red-700">
                          {qontoSubmitError}
                        </div>
                      ) : null}

                      {qontoSubmitInfo ? (
                        <div className="rounded-2xl border border-green-200 bg-green-50 p-4 mb-6 text-sm text-green-800">
                          Übergabe erfolgreich: {qontoSubmitInfo.createdCount} neu
                          erstellt, {qontoSubmitInfo.updatedCount} aktualisiert,{" "}
                          {qontoSubmitInfo.skippedCount} übersprungen.
                        </div>
                      ) : null}

                      <div className="rounded-2xl border border-gray-200 bg-white p-4 mb-6">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                          <div className="flex items-center gap-3 flex-wrap">
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={allSelectableChecked}
                                onChange={(e) =>
                                  toggleAllQontoInvoices(e.target.checked)
                                }
                              />
                              <span>Alle auswählbaren markieren</span>
                            </label>

                            <Badge
                              kind={selectedQontoIds.length ? "blue" : "default"}
                            >
                              {selectedQontoIds.length} ausgewählt
                            </Badge>
                          </div>

                          <button
                            onClick={submitSelectedQontoToInkasso}
                            disabled={qontoSubmitting || !selectedQontoIds.length}
                            type="button"
                            className="px-5 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            {qontoSubmitting
                              ? "Übergabe läuft ..."
                              : "Ausgewählte an Inkasso übergeben"}
                          </button>
                        </div>
                      </div>

                      {qontoCases.length === 0 ? (
                        <EmptyState
                          title="Keine Qonto Inkasso-Kandidaten gefunden"
                          text="Aktuell gibt es keine überfälligen, unbezahlten und geeigneten Rechnungen in der Qonto-Auswahl."
                        />
                      ) : (
                        <div className="overflow-x-auto rounded-2xl border border-gray-200">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr className="text-left text-gray-600">
                                <th className="px-4 py-3 font-semibold w-12">
                                  <input
                                    type="checkbox"
                                    checked={allSelectableChecked}
                                    onChange={(e) =>
                                      toggleAllQontoInvoices(e.target.checked)
                                    }
                                  />
                                </th>
                                <th className="px-4 py-3 font-semibold">
                                  Rechnung
                                </th>
                                <th className="px-4 py-3 font-semibold">Kunde</th>
                                <th className="px-4 py-3 font-semibold">Betrag</th>
                                <th className="px-4 py-3 font-semibold">Fällig</th>
                                <th className="px-4 py-3 font-semibold">
                                  Überfällig
                                </th>
                                <th className="px-4 py-3 font-semibold">
                                  Reminder
                                </th>
                                <th className="px-4 py-3 font-semibold">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {qontoCases.map((item) => {
                                const rowId = getQontoRowId(item);
                                const isSubmitted = !!submittedQontoIds[rowId];
                                const isChecked = selectedQontoIds.includes(rowId);

                                return (
                                  <tr key={rowId || item.invoice_number}>
                                    <td className="px-4 py-4 align-top">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        disabled={isSubmitted || !rowId}
                                        onChange={() => toggleQontoInvoice(rowId)}
                                      />
                                    </td>

                                    <td className="px-4 py-4 align-top">
                                      <div className="font-semibold text-gray-900">
                                        {item.invoice_number || "-"}
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1 break-all">
                                        {item.external_invoice_id || "-"}
                                      </div>
                                    </td>

                                    <td className="px-4 py-4 align-top">
                                      <div className="text-gray-900">
                                        {item.customer_name || "-"}
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1">
                                        {item.customer_email || "-"}
                                      </div>
                                    </td>

                                    <td className="px-4 py-4 align-top font-medium text-gray-900">
                                      {formatCurrency(
                                        item.amount_total,
                                        item.currency || "EUR"
                                      )}
                                    </td>

                                    <td className="px-4 py-4 align-top text-gray-700">
                                      {formatDate(item.due_date)}
                                    </td>

                                    <td className="px-4 py-4 align-top">
                                      <Badge
                                        kind={
                                          (item.days_overdue || 0) > 30
                                            ? "red"
                                            : "yellow"
                                        }
                                      >
                                        {item.days_overdue || 0} Tage
                                      </Badge>
                                    </td>

                                    <td className="px-4 py-4 align-top">
                                      <Badge kind="blue">
                                        Level {item.derived_reminder_level ?? 0}
                                      </Badge>
                                    </td>

                                    <td className="px-4 py-4 align-top">
                                      {isSubmitted ? (
                                        <Badge kind="green">Übergeben</Badge>
                                      ) : isChecked ? (
                                        <Badge kind="blue">Ausgewählt</Badge>
                                      ) : (
                                        <Badge kind="red">Inkasso-Kandidat</Badge>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {selectedProvider === "cobot" && (
                <div>
                  <div className="flex items-center justify-between gap-3 mb-5">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Cobot</h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Bereich für den aktuell gespeicherten Cobot-Kontext und das
                        Firmenprofil.
                      </p>
                    </div>
                    <ProviderTypeBadge provider="cobot" />
                  </div>

                  {!companyProfile ? (
                    <EmptyState
                      title="Noch kein Cobot-Profil geladen"
                      text="Bitte das Firmenprofil öffnen und speichern."
                    />
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="rounded-2xl border border-gray-200 p-5 bg-gray-50">
                        <div className="text-sm text-gray-500">Space Name</div>
                        <div className="mt-1 text-lg font-semibold text-gray-900">
                          {companyProfile.space_name || "-"}
                        </div>
                      </div>

                      <div className="rounded-2xl border border-gray-200 p-5 bg-gray-50">
                        <div className="text-sm text-gray-500">Anchor / Context</div>
                        <div className="mt-1 font-mono text-gray-900 break-all">
                          {companyProfile.anchor_space_id ||
                            companyProfile.space_id ||
                            spaceId ||
                            "-"}
                        </div>
                      </div>

                      <div className="rounded-2xl border border-gray-200 p-5 bg-gray-50">
                        <div className="text-sm text-gray-500">Cobot Space ID</div>
                        <div className="mt-1 font-mono text-gray-900 break-all">
                          {companyProfile.cobot_space_id || "-"}
                        </div>
                      </div>

                      <div className="rounded-2xl border border-gray-200 p-5 bg-gray-50">
                        <div className="text-sm text-gray-500">
                          Profil aktualisiert
                        </div>
                        <div className="mt-1 text-gray-900">
                          {formatDate(companyProfile.updated_at)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedProvider === "cobot-invoices" && (
                <div>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        Cobot Rechnungen
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Offene Rechnungen aus Cobot mit Filter nach Alter und
                        Mahnstufe.
                      </p>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      <ProviderTypeBadge provider="cobot" />
                      <Badge kind="green">{getEffectiveSpaceId() || "-"}</Badge>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Mindestalter in Tagen
                        </label>
                        <input
                           type="number"
                           min="0"
                           name="minAgeDays"
                           value={cobotFilters.minAgeDays}
                           placeholder="z. B. 20"
                           onChange={handleCobotFilterChange}
                           className="w-full border border-gray-300 rounded-xl p-3"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Mindest-Mahnstufe
                        </label>
                      <input
                          type="number"
                          min="0"
                          name="minReminderLevel"
                           value={cobotFilters.minReminderLevel}
                           placeholder="z. B. 1"
                           onChange={handleCobotFilterChange}
                           className="w-full border border-gray-300 rounded-xl p-3"
                          />
                      </div>

                      <div className="md:col-span-2 flex items-end">
                        <button
                          onClick={handleLoadCobotInvoices}
                          type="button"
                          disabled={cobotLoading || !getEffectiveSpaceId()}
                          className="w-full md:w-auto px-5 py-3 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          {cobotLoading
                            ? "Rechnungen werden geladen ..."
                            : "Cobot Rechnungen laden"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {cobotMeta && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <StatCard
                        title="Space"
                        value={cobotMeta.requested_space || getEffectiveSpaceId() || "-"}
                        hint="Abgefragter Cobot-Space"
                        kind="green"
                      />
                      <StatCard
                        title="Treffer"
                        value={cobotMeta.filtered ?? cobotInvoices.length}
                        hint={`Total: ${cobotMeta.total ?? cobotInvoices.length}`}
                        kind="green"
                      />
                      <StatCard
                        title="Volumen"
                        value={formatCurrency(cobotTotalAmount, "EUR")}
                        hint="Summe der geladenen Rechnungen"
                        kind="yellow"
                      />
                      <StatCard
                        title="Ausgewählt"
                        value={selectedCobotIds.length}
                        hint={formatCurrency(cobotSelectedAmount, "EUR")}
                        kind="blue"
                      />
                    </div>
                  )}

                  {cobotLoading && (
                    <div className="rounded-2xl border border-green-200 bg-green-50 p-5 text-sm text-green-900">
                      Cobot-Rechnungen werden geladen ...
                    </div>
                  )}

                  {!cobotLoading && cobotError && (
                    <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
                      {cobotError}
                    </div>
                  )}

                  {!cobotLoading && cobotSubmitError && (
                    <div className="rounded-2xl border border-red-200 bg-red-50 p-4 mb-6 text-sm text-red-700">
                      {cobotSubmitError}
                    </div>
                  )}

                  {!cobotLoading && cobotSubmitInfo && (
                    <div className="rounded-2xl border border-green-200 bg-green-50 p-4 mb-6 text-sm text-green-800">
                      Übergabe verarbeitet: {cobotSubmitInfo.createdCount} neu
                      erstellt, {cobotSubmitInfo.duplicateCount} bereits vorhanden,{" "}
                      {cobotSubmitInfo.errorCount} mit Fehler.
                    </div>
                  )}

                  {!cobotLoading && !cobotError && cobotInvoices.length === 0 && (
                    <EmptyState
                      title="Noch keine Cobot-Rechnungen geladen"
                      text="Nutze die Filter oben und lade anschließend die offenen Rechnungen aus Cobot."
                    />
                  )}

                  {!cobotLoading && !cobotError && cobotInvoices.length > 0 && (
                    <>
                      <div className="rounded-2xl border border-gray-200 bg-white p-4 mb-6">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                          <div className="flex items-center gap-3 flex-wrap">
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={allSelectableCobotChecked}
                                onChange={(e) =>
                                  toggleAllCobotInvoices(e.target.checked)
                                }
                              />
                              <span>Alle auswählbaren markieren</span>
                            </label>

                            <Badge
                              kind={selectedCobotIds.length ? "blue" : "default"}
                            >
                              {selectedCobotIds.length} ausgewählt
                            </Badge>
                          </div>

                          <button
                            onClick={() => submitSelectedCobotToInkasso()}
                            disabled={cobotSubmitting || !selectedCobotIds.length}
                            type="button"
                            className="px-5 py-3 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            {cobotSubmitting
                              ? "Übergabe läuft ..."
                              : "Ausgewählte an Inkasso übergeben"}
                          </button>
                        </div>
                      </div>

                      <div className="overflow-x-auto rounded-2xl border border-gray-200">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr className="text-left text-gray-600">
                              <th className="px-4 py-3 font-semibold w-12">
                                <input
                                  type="checkbox"
                                  checked={allSelectableCobotChecked}
                                  onChange={(e) =>
                                    toggleAllCobotInvoices(e.target.checked)
                                  }
                                />
                              </th>
                              <th className="px-4 py-3 font-semibold">Rechnung</th>
                              <th className="px-4 py-3 font-semibold">Kunde</th>
                              <th className="px-4 py-3 font-semibold">Betrag</th>
                              <th className="px-4 py-3 font-semibold">Fällig / Referenz</th>
                              <th className="px-4 py-3 font-semibold">Mahnstufe</th>
                              <th className="px-4 py-3 font-semibold">Status</th>
                              <th className="px-4 py-3 font-semibold">ID</th>
                              <th className="px-4 py-3 font-semibold">Aktion</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {cobotInvoices.map((item) => {
                              const rowId = getCobotInvoiceId(item);
                              const isSubmitted = !!submittedCobotIds[rowId];
                              const isChecked = selectedCobotIds.includes(rowId);
                              const isTransferable = isCobotTransferable(
                                item,
                                isSubmitted
                              );
                              const rowStatus = getCobotRowStatus(item, isSubmitted);

                              return (
                                <tr key={rowId || getCobotInvoiceNumber(item)}>
                                  <td className="px-4 py-4 align-top">
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      disabled={!isTransferable}
                                      onChange={() => toggleCobotInvoice(rowId)}
                                    />
                                  </td>

                                  <td className="px-4 py-4 align-top">
                                    <div className="font-semibold text-gray-900">
                                      {getCobotInvoiceNumber(item)}
                                    </div>
                                  </td>

                                  <td className="px-4 py-4 align-top">
                                    <div className="text-gray-900">
                                      {getCobotCustomerName(item)}
                                    </div>
                                  </td>

                                  <td className="px-4 py-4 align-top font-medium text-gray-900">
                                    {formatCurrency(
                                      getCobotAmount(item),
                                      getCobotCurrency(item)
                                    )}
                                  </td>

                                  <td className="px-4 py-4 align-top text-gray-700">
                                    {formatDate(getCobotDueDate(item))}
                                  </td>

                                  <td className="px-4 py-4 align-top">
                                    <Badge
                                      kind={
                                        getCobotReminderLevel(item) > 0 ? "yellow" : "default"
                                      }
                                    >
                                      Level {getCobotReminderLevel(item)}
                                    </Badge>
                                  </td>

                                  <td className="px-4 py-4 align-top">
                                    {rowStatus === "submitted" ? (
                                      <Badge kind="green">Übergeben</Badge>
                                    ) : isChecked ? (
                                      <Badge kind="blue">Ausgewählt</Badge>
                                    ) : isTransferable ? (
                                      <Badge kind="red">Inkasso-Kandidat</Badge>
                                    ) : (
                                      <Badge kind="default">
                                        {item?.inkasso_status || "nicht bereit"}
                                      </Badge>
                                    )}
                                  </td>

                                  <td className="px-4 py-4 align-top">
                                    <div className="font-mono text-xs text-gray-500 break-all max-w-[220px]">
                                      {rowId || "-"}
                                    </div>
                                  </td>

                                  <td className="px-4 py-4 align-top">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        submitSelectedCobotToInkasso([rowId])
                                      }
                                      disabled={!isTransferable || cobotSubmitting}
                                      className="px-3 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                    >
                                      Übergabe an Inkasso
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}

              {selectedProvider === "cases" && (
                <div>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        Inkasso Übersicht
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Kompakte Kennzahlen zum aktuellen Aktenbestand. Die
                        vollständige Liste bleibt in der separaten Aktenübersicht.
                      </p>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      <Badge kind="yellow">Mandantensicht</Badge>

                      <button
                        type="button"
                        onClick={handleExportMandantCasesCSV}
                        className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        CSV herunterladen
                      </button>

                      <button
                        type="button"
                        onClick={() => navigate("/inkasso/akten")}
                        className="px-4 py-2 rounded-lg bg-yellow-500 text-white text-sm font-medium hover:bg-yellow-600"
                      >
                        Zur Aktenübersicht
                      </button>
                    </div>
                  </div>

                  {mandantCasesLoading && (
                    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 text-sm text-gray-700 mb-6">
                      Aktenvorschau wird geladen ...
                    </div>
                  )}

                  {!mandantCasesLoading && mandantCasesError && (
                    <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700 mb-6">
                      {mandantCasesError}
                    </div>
                  )}

                  {!mandantCasesLoading && !mandantCasesError && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                        <StatCard
                          title="Offene Akten"
                          value={mandantCasesStats.offeneAkten}
                          hint="Aktueller offener Bestand"
                          kind="yellow"
                        />
                        <StatCard
                          title="Offene Hauptforderung"
                          value={formatCurrency(mandantCasesStats.offeneHf, "EUR")}
                          hint="Summierter offener Betrag"
                          kind="green"
                        />
                        <StatCard
                          title="Offene Kosten"
                          value={formatCurrency(
                            mandantCasesStats.offeneKosten,
                            "EUR"
                          )}
                          hint="Summierte offene Kosten"
                          kind="blue"
                        />
                        <StatCard
                          title="Gesamt offen"
                          value={formatCurrency(
                            mandantCasesStats.offeneGesamt,
                            "EUR"
                          )}
                          hint="Summe aus importierten Gesamtwerten"
                          kind="yellow"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
                        <div className="rounded-2xl border border-gray-200 p-5 bg-gray-50">
                          <div className="text-sm text-gray-500">
                            Schuldner gesamt
                          </div>
                          <div className="mt-2 text-2xl font-bold text-gray-900">
                            {mandantCasesStats.schuldnerAnzahl || 0}
                          </div>
                        </div>

                        <div className="rounded-2xl border border-gray-200 p-5 bg-gray-50">
                          <div className="text-sm text-gray-500">
                            Letzte Aktualisierung
                          </div>
                          <div className="mt-2 text-lg font-semibold text-gray-900">
                            {formatShortDateTime(
                              mandantCasesStats.letzteAktualisierung
                            )}
                          </div>
                        </div>

                        <div className="rounded-2xl border border-dashed border-gray-300 p-5 bg-yellow-50">
                          <div className="text-sm font-semibold text-gray-900">
                            Aktenliste ausgelagert
                          </div>
                          <p className="mt-2 text-sm text-gray-600">
                            Im Dashboard zeigen wir bewusst keine einzelnen Akten
                            mehr an. Für Suche, Filter und vollständige Liste nutzt
                            du die Aktenübersicht.
                          </p>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-5">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                          <div>
                            <div className="text-base font-semibold text-gray-900">
                              Vollständige Aktenliste
                            </div>
                            <p className="mt-1 text-sm text-gray-500">
                              Über die Aktenübersicht gelangst du in die
                              vollständige Liste mit Suche und Filtern.
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => navigate("/inkasso/akten")}
                            className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                          >
                            Aktenübersicht öffnen
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyDashboard;